/**
 * Re-audit — start a new run from an existing audit, copying pack selection.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, AuditRunListItem, ReauditListItem } from '../lib/api'
import { LoadingSpinner } from '../components/design-system/Loading'
import { EmptyState } from '../components/design-system/EmptyState'
import { Button } from '../components/design-system/Button'
import { StatusBadge } from '../components/design-system/Badge'
import { useLocale } from '../contexts/LocaleContext'

export function ReauditPage() {
  const { t } = useLocale()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [audits, setAudits] = useState<AuditRunListItem[]>([])
  const [reaudits, setReaudits] = useState<ReauditListItem[]>([])
  const [selectedAuditId, setSelectedAuditId] = useState(searchParams.get('audit_id') || '')
  const [quickScan, setQuickScan] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  async function load() {
    const [auditData, reauditData] = await Promise.all([
      api.listAuditRuns({ page: 1, page_size: 50 }),
      api.listReaudits({ limit: 50 }),
    ])
    setAudits(auditData.audits)
    setReaudits(reauditData.reaudits)
    if (!selectedAuditId) {
      const eligible = auditData.audits.find((item) =>
        ['completed', 'partial', 'failed'].includes(item.status)
      )
      if (eligible) setSelectedAuditId(eligible.id)
    }
  }

  useEffect(() => {
    load()
      .catch((err) => setError(err instanceof Error ? err.message : t('reaudit.failed')))
      .finally(() => setLoading(false))
  }, [])

  async function handleStart() {
    if (!selectedAuditId) {
      setError(t('reaudit.selectAudit'))
      return
    }
    try {
      setSubmitting(true)
      setError(null)
      const result = await api.triggerReauditFromAudit({
        original_audit_run_id: selectedAuditId,
        quick_scan: quickScan,
      })
      navigate(`/audit/${result.new_audit_run_id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('reaudit.failed'))
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div data-testid="reaudit-page" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <h1 style={h1Style}>{t('reaudit.title')}</h1>
        <div style={{ display: 'flex', justifyContent: 'center', minHeight: '240px', alignItems: 'center' }}>
          <LoadingSpinner size="lg" text={t('reaudit.loading')} />
        </div>
      </div>
    )
  }

  const eligible = audits.filter((item) => ['completed', 'partial', 'failed'].includes(item.status))

  return (
    <div data-testid="reaudit-page" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <header>
        <h1 style={h1Style}>{t('reaudit.title')}</h1>
        <p style={subStyle}>
          {t('reaudit.lead')}
        </p>
      </header>

      {error && <div className="card card-error"><p className="text-sm">{error}</p></div>}

      {eligible.length === 0 ? (
          <EmptyState
          title={t('reaudit.empty')}
          description={t('reaudit.emptyBody')}
          action={{ label: t('scores.goAudits'), onClick: () => navigate('/audits') }}
        />
      ) : (
        <section style={cardStyle}>
          <h2 style={h2Style}>{t('reaudit.startFrom')}</h2>
          <label className="text-sm" style={labelStyle}>
            {t('reaudit.original')}
            <select
              value={selectedAuditId}
              onChange={(e) => setSelectedAuditId(e.target.value)}
              data-testid="reaudit-audit-select"
              style={inputStyle}
            >
              {eligible.map((audit) => (
                <option key={audit.id} value={audit.id}>
                  {audit.project_name} · {audit.status} · {audit.id.slice(0, 8)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="checkbox" checked={quickScan} onChange={(e) => setQuickScan(e.target.checked)} />
            {t('reaudit.quick')}
          </label>
          <Button onClick={handleStart} disabled={submitting} data-testid="reaudit-submit">
            {submitting ? t('reaudit.starting') : t('reaudit.start')}
          </Button>
        </section>
      )}

      <section>
        <h2 style={h2Style}>{t('reaudit.history')}</h2>
        {reaudits.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--reviewer-text-secondary)' }}>{t('reaudit.none')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {reaudits.map((item) => (
              <button
                key={item.reaudit_id}
                onClick={() => navigate(`/audit/${item.new_audit_id}`)}
                style={rowStyle}
              >
                <StatusBadge status={item.status} />
                <span>{t('reaudit.new', { id: item.new_audit_id.slice(0, 8) })}</span>
                {item.original_audit_id && <span>{t('reaudit.from', { id: item.original_audit_id.slice(0, 8) })}</span>}
                {item.net_improvement_score != null && <span>{t('reaudit.score', { n: item.net_improvement_score })}</span>}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

const h1Style = { margin: 0, marginBottom: '8px', fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-2xl)', fontWeight: 500 } as const
const h2Style = { margin: '0 0 12px', fontFamily: 'var(--reviewer-font-mono)', fontSize: '16px' } as const
const subStyle = { margin: 0, fontFamily: 'var(--reviewer-font-mono)', color: 'var(--reviewer-text-secondary)' } as const
const cardStyle = { padding: '20px', border: '1px solid var(--reviewer-border)', backgroundColor: 'var(--reviewer-surface)', display: 'flex', flexDirection: 'column' as const, gap: '12px' }
const labelStyle = { display: 'flex', flexDirection: 'column' as const, gap: '6px' }
const inputStyle = { padding: '8px 12px', fontFamily: 'var(--reviewer-font-mono)', backgroundColor: 'var(--reviewer-bg)', border: '1px solid var(--reviewer-border)', color: 'var(--reviewer-text)' } as const
const rowStyle = { display: 'flex', gap: '12px', alignItems: 'center', width: '100%', textAlign: 'left' as const, padding: '10px 12px', border: '1px solid var(--reviewer-border)', background: 'var(--reviewer-surface)', color: 'var(--reviewer-text)', cursor: 'pointer', fontFamily: 'var(--reviewer-font-mono)' }
